import { useCallback, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'
import { CalendarEvent, type AudioNote } from '@highlight/common'
import { useCalendars, type Calendar } from '@renderer/hooks/use-calendars'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { getCalendarCreateUrl, getProviderForAccount } from '@renderer/utils/calendar-event-creation'
import { useTodaysMeetingAudioNotes } from '@renderer/hooks/audio-notes'
import { useHasLiveAudioNote } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useCalendarEventForCurrentMeeting } from '@renderer/hooks/audio/use-recording-calendar-event'
import { isDayToday, compareMeetingsLiveFirst } from '@shared/utils/DateUtils'
import { isNoteForCalendarEvent } from '@shared/utils/audio-notes/AudioNoteUtils'
import { MEETING_VIEW_ID } from '../../components/meeting/meeting-view-id'
import type { ViewFilterOption } from '../../components/view-filter-menu'
import type { RecentActivity } from '../../types'
import { useNextWeekCalendarEvents } from '../../hooks/use-upcoming-meetings'
import { useViewActions, type ViewOpenTarget } from '../../view-atoms'
import { getDateGroupLabel, groupUpcomingEvents } from './group-upcoming-events'

const ALL_CALENDARS = 'all'

function eventMatchesQuery(event: CalendarEvent, query: string): boolean {
  return event.title.toLowerCase().includes(query) || event.attendees.some((a) => a.email.toLowerCase().includes(query))
}

/** Persisted so reopening the view lands on the calendar the user last chose. */
const upcomingCalendarFilterAtom = atomWithGlobalStore<string>('general', 'sidepanel.upcoming.filter', ALL_CALENDARS)

/** One "Earlier Today" row — a recorded note, or a past event nothing was recorded for. */
export type EarlierTodayItem = { type: 'note'; note: RecentActivity } | { type: 'event'; event: CalendarEvent }

/** One labelled block of the Upcoming list, in the order it renders. */
export type UpcomingSection =
  | { kind: 'earlier-today'; label: string; items: Array<EarlierTodayItem> }
  | { kind: 'clear-day'; label: string }
  | { kind: 'events'; label: string; events: Array<CalendarEvent> }

export interface UpcomingViewModel {
  isLoading: boolean
  searchQuery: string
  setSearchQuery: (query: string) => void
  /** `ALL_CALENDARS`, or the id of one connected calendar. */
  calendarFilter: string
  setCalendarFilter: (filter: string) => void
  /**
   * `All` plus one entry per enabled calendar. Empty when the user has fewer
   * than two — a chip whose only option is "All" filters nothing, so an empty
   * list is also the view's signal not to render the chip at all.
   */
  calendarOptions: Array<ViewFilterOption<string>>
  /** Empty means there is nothing to show — the view renders its empty state. */
  sections: Array<UpcomingSection>
  calendars: Array<Calendar> | undefined
  /**
   * The event holding the live note right now, if it's one of the listed ones.
   * Paused and saving count: the row stays live rather than reverting to a
   * popover row mid-recording.
   */
  activeTranscriptEventId: string | null
  createEvent: () => void
  openMeetingNote: (note: RecentActivity, open: ViewOpenTarget) => void
}

export function useUpcomingViewModel(): UpcomingViewModel {
  const { data: calendarEvents, isLoading: isLoadingEvents } = useNextWeekCalendarEvents()
  const { data: audioNotesData, isLoading: isLoadingAudioNotes } = useTodaysMeetingAudioNotes()
  const { openView } = useViewActions()
  const { defaultAccount, googleAccounts, outlookAccounts, calendars } = useCalendars()
  const [searchQuery, setSearchQuery] = useState('')
  const [storedCalendarFilter, setCalendarFilter] = useAtom(upcomingCalendarFilterAtom)
  // `useHasLiveAudioNote`, not `useIsTakingNotes`: the latter drops paused and
  // saving, which would take the live row away mid-recording. Matches Home's
  // `SidepanelUpcomingList`, which gates the same id on the same predicate.
  const hasLiveNote = useHasLiveAudioNote()
  // Resolved through the shared link predicate rather than the stored
  // calendarEventId, which is still empty in the window before main links a
  // detected meeting to its event (HIG-5856).
  const activeMeetingEvent = useCalendarEventForCurrentMeeting(calendarEvents)
  const activeTranscriptEventId = hasLiveNote ? (activeMeetingEvent?.id ?? null) : null

  const createEvent = useCallback(() => {
    const provider = getProviderForAccount(defaultAccount, googleAccounts, outlookAccounts)
    if (!provider) return

    AmplitudeRenderer.trackEvent('HLC Create Button Action', {
      action: 'Create Event',
      trigger: 'Sidepanel-Drawer',
      provider,
    })

    const url = getCalendarCreateUrl(provider, defaultAccount)
    window.open(url, '_blank')
  }, [defaultAccount, googleAccounts, outlookAccounts])

  // Earlier Today is the day's calendar history, so a recorded note only
  // belongs when it's tied to a scheduled meeting — linked by id, or matching
  // one of today's events by title + time overlap. Ad-hoc recordings with no
  // calendar event stay out (they live in Recents).
  const recentMeetingNotes = useMemo<Array<AudioNote>>(() => {
    if (!audioNotesData) return []
    const now = Date.now()
    const events = calendarEvents ?? []

    return audioNotesData
      .filter((note) => {
        if (!note.meeting) return false
        const endedAt = new Date(note.endedAt)
        if (endedAt.getTime() > now || !isDayToday(endedAt)) return false
        return Boolean(note.meeting.calendarEventId) || events.some((event) => isNoteForCalendarEvent(note, event))
      })
      .sort((a, b) => new Date(b.endedAt).getTime() - new Date(a.endedAt).getTime())
  }, [audioNotesData, calendarEvents])

  // Only the calendars actually feeding the list are worth offering, and only
  // once there's more than one — otherwise the chip's sole option is "All".
  const calendarOptions = useMemo<Array<ViewFilterOption<string>>>(() => {
    const enabled = (calendars ?? []).filter((calendar) => calendar.metadata?.enabled === true)
    if (enabled.length < 2) return []
    return [
      { value: ALL_CALENDARS, label: 'All' },
      ...enabled.map((calendar) => ({ value: calendar.id, label: calendar.name })),
    ]
  }, [calendars])

  // A calendar that's been disconnected or disabled since it was picked would
  // filter the list down to nothing with no way to tell why.
  const calendarFilter = calendarOptions.some((option) => option.value === storedCalendarFilter)
    ? storedCalendarFilter
    : ALL_CALENDARS

  // Recorded notes have no calendar to attribute them to, so scoping to one
  // calendar drops them rather than leaving unrelated rows above a filtered
  // list — the meetings behind them fall back to plain past-event rows.
  const recentMeetingNotesInScope = useMemo(
    () => (calendarFilter === ALL_CALENDARS ? recentMeetingNotes : []),
    [recentMeetingNotes, calendarFilter],
  )

  const filteredRecentMeetingNotes = useMemo(() => {
    if (!searchQuery.trim()) return recentMeetingNotesInScope

    const query = searchQuery.toLowerCase()
    return recentMeetingNotesInScope.filter((note) => {
      if (note.title.toLowerCase().includes(query)) return true
      if (note.meeting?.windowTitle?.toLowerCase().includes(query)) return true
      return false
    })
  }, [recentMeetingNotesInScope, searchQuery])

  // Calendar event ids already represented by a rendered "Earlier Today" note,
  // so we can drop them from the event list and avoid showing the same meeting
  // twice (the event can still be "upcoming" if its recording ended before the
  // scheduled end time). Built from the notes that actually render — a note
  // hidden by the calendar filter or search must not also hide its event, or
  // the meeting would vanish from the list entirely.
  const recordedEventIds = useMemo(() => {
    const ids = new Set<string>()
    for (const note of filteredRecentMeetingNotes) {
      const id = note.meeting?.calendarEventId
      if (id) ids.add(id)
    }
    return ids
  }, [filteredRecentMeetingNotes])

  // Fallback dedupe by time-range + title. Checks all rendered notes (not just
  // those missing a calendarEventId) so a note recorded against a duplicate
  // event (e.g. the same meeting synced from two calendars) still hides this one.
  const matchesRecordedNote = useCallback(
    (event: CalendarEvent) => filteredRecentMeetingNotes.some((note) => isNoteForCalendarEvent(note, event)),
    [filteredRecentMeetingNotes],
  )

  // One pass decides what's visible at all — recorded events are covered by
  // their "Earlier Today" note, and the calendar filter and search scope the
  // rest. Both halves of the list share these rules, so they're applied once.
  const visibleEvents = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return (calendarEvents ?? []).filter((event) => {
      if (recordedEventIds.has(event.id)) return false
      if (matchesRecordedNote(event)) return false
      if (calendarFilter !== ALL_CALENDARS && event.calendarId !== calendarFilter) return false
      if (query && !eventMatchesQuery(event, query)) return false
      return true
    })
  }, [calendarEvents, recordedEventIds, matchesRecordedNote, calendarFilter, searchQuery])

  // Split at now: the future half is the upcoming list, and the past half joins
  // "Earlier Today" — the section is the day's history, not just what was
  // captured. The calendar feed already starts at midnight today, so anything
  // ended by now started today. One partition (not two filters) so an event
  // ending this instant can't land in both halves.
  const { pastTodayEvents, upcomingInScope } = useMemo(() => {
    const now = new Date()
    const past: Array<CalendarEvent> = []
    const upcoming: Array<CalendarEvent> = []
    for (const event of visibleEvents) {
      ;((event.end || event.start) > now ? upcoming : past).push(event)
    }
    return { pastTodayEvents: past, upcomingInScope: upcoming }
  }, [visibleEvents])

  const filteredEvents = useMemo(
    () => [...upcomingInScope].sort(compareMeetingsLiveFirst(activeTranscriptEventId)),
    [upcomingInScope, activeTranscriptEventId],
  )

  const hasTodayEvents = useMemo(
    () => upcomingInScope.some((event) => getDateGroupLabel(new Date(event.start), new Date()) === 'Today'),
    [upcomingInScope],
  )

  // Earlier Today renders the same flat row Recents does, so the notes are
  // mapped into the `RecentActivity` shape that row reads.
  const recentMeetingRows = useMemo<Array<RecentActivity>>(
    () =>
      filteredRecentMeetingNotes.map((note) => ({
        id: note.id,
        type: 'audio-note' as const,
        title: note.title,
        unifiedUpdatedAt: note.endedAt,
        meetingSource: note.meeting?.app?.name,
      })),
    [filteredRecentMeetingNotes],
  )

  // One list, most recently ended first, so a recorded meeting and the
  // unrecorded event before it read in the order the day actually happened.
  const earlierTodayItems = useMemo<Array<EarlierTodayItem>>(() => {
    const endedAt = (item: EarlierTodayItem) =>
      item.type === 'note'
        ? new Date(item.note.unifiedUpdatedAt).getTime()
        : new Date(item.event.end || item.event.start).getTime()

    return [
      ...recentMeetingRows.map((note) => ({ type: 'note' as const, note })),
      ...pastTodayEvents.map((event) => ({ type: 'event' as const, event })),
    ].sort((a, b) => endedAt(b) - endedAt(a))
  }, [recentMeetingRows, pastTodayEvents])

  const openMeetingNote = useCallback(
    (note: RecentActivity, open: ViewOpenTarget) => {
      openView(
        MEETING_VIEW_ID,
        { mode: 'note', audioNoteId: note.id, title: note.title, initialTab: 'recap' },
        { sourceLabel: 'Earlier Today', ...open },
      )
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'view summary',
        trigger: 'sidepanel-drawer-earlier-today',
      })
    },
    [openView],
  )

  // The view renders one list of labelled sections, so the model decides what
  // those sections are and in what order. Keeping the sequencing here is what
  // lets the view render them with a single map — the alternative was three
  // conditional blocks and a boolean expression deciding which label was first.
  const sections = useMemo<Array<UpcomingSection>>(() => {
    if (filteredEvents.length === 0 && earlierTodayItems.length === 0) {
      return []
    }

    const list: Array<UpcomingSection> = []
    if (earlierTodayItems.length > 0) {
      list.push({ kind: 'earlier-today', label: 'Earlier Today', items: earlierTodayItems })
    }
    // Nothing left today, and not a filtered view of the week: say so where
    // today's meetings would have been.
    if (!hasTodayEvents && !searchQuery) {
      list.push({ kind: 'clear-day', label: 'Today' })
    }
    for (const group of groupUpcomingEvents(filteredEvents, new Date())) {
      list.push({ kind: 'events', label: group.label, events: group.events })
    }
    return list
  }, [filteredEvents, earlierTodayItems, hasTodayEvents, searchQuery])

  return {
    isLoading: isLoadingEvents || isLoadingAudioNotes,
    searchQuery,
    setSearchQuery,
    calendarFilter,
    setCalendarFilter,
    calendarOptions,
    sections,
    calendars,
    activeTranscriptEventId,
    createEvent,
    openMeetingNote,
  }
}
